import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import axios from "axios";
import { Plus, Edit, Trash2, Briefcase, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FunctionsPage() {
  const { isAdmin, currentBranch } = useAuth();
  const [functions, setFunctions] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFunction, setEditingFunction] = useState(null);
  const [form, setForm] = useState({ nome: "", descricao: "", ministerios_ids: [] });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [functionsRes, ministriesRes] = await Promise.all([
        axios.get(`${API}/functions`),
        axios.get(`${API}/ministries`)
      ]);
      setFunctions(functionsRes.data);
      setMinistries(ministriesRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        filial_id: currentBranch?.id || null
      };
      if (editingFunction) {
        await axios.put(`${API}/functions/${editingFunction.id}`, payload);
        toast.success("Função atualizada!");
      } else {
        await axios.post(`${API}/functions`, payload);
        toast.success("Função criada!");
      }
      setDialogOpen(false);
      setEditingFunction(null);
      setForm({ nome: "", descricao: "", ministerios_ids: [] });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar");
    }
  };

  const handleEdit = (func) => {
    setEditingFunction(func);
    setForm({
      nome: func.nome,
      descricao: func.descricao || "",
      ministerios_ids: func.ministerios_ids || (func.ministerio_id ? [func.ministerio_id] : [])
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja remover esta função?")) return;
    try {
      await axios.delete(`${API}/functions/${id}`);
      toast.success("Função removida");
      loadData();
    } catch (error) {
      toast.error("Erro ao remover");
    }
  };

  const toggleMinistry = (ministryId) => {
    const isSelected = form.ministerios_ids.includes(ministryId);
    if (isSelected) {
      setForm({
        ...form,
        ministerios_ids: form.ministerios_ids.filter(id => id !== ministryId)
      });
    } else {
      setForm({
        ...form,
        ministerios_ids: [...form.ministerios_ids, ministryId]
      });
    }
  };

  const getMinistryNames = (func) => {
    const ids = func.ministerios_ids || (func.ministerio_id ? [func.ministerio_id] : []);
    return ids.map(id => ministries.find(m => m.id === id)?.nome).filter(Boolean);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="funcoes-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Funções</h1>
        {isAdmin() && (
          <Button onClick={() => { setEditingFunction(null); setForm({ nome: "", descricao: "", ministerios_ids: [] }); setDialogOpen(true); }} data-testid="add-function-button">
            <Plus className="h-4 w-4 mr-2" />
            Adicionar
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {functions.length === 0 ? (
          <p className="text-muted-foreground col-span-full text-center py-8">Nenhuma função cadastrada</p>
        ) : (
          functions.map((func) => (
            <Card key={func.id} className="card-hover" data-testid={`function-card-${func.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-xl bg-primary/10">
                      <Briefcase className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">{func.nome}</h3>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {getMinistryNames(func).map((name, idx) => (
                          <Badge key={idx} variant="secondary" className="text-xs">
                            {name}
                          </Badge>
                        ))}
                        {getMinistryNames(func).length === 0 && (
                          <span className="text-xs text-muted-foreground">Sem ministério</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {isAdmin() && (
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" onClick={() => handleEdit(func)} data-testid={`edit-function-${func.id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleDelete(func.id)} data-testid={`delete-function-${func.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
                
                {func.descricao && (
                  <p className="text-sm text-muted-foreground mt-3">{func.descricao}</p>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingFunction ? "Editar Função" : "Nova Função"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome *</Label>
              <Input
                required
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                data-testid="function-nome"
              />
            </div>

            <div className="space-y-2">
              <Label>Descrição</Label>
              <Input
                value={form.descricao || ""}
                onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                data-testid="function-descricao"
              />
            </div>

            {/* Múltipla seleção de Ministérios */}
            <div className="space-y-2">
              <Label>Ministérios</Label>
              <div className="border rounded-lg p-3 space-y-2 bg-muted/30">
                <div className="flex flex-wrap gap-2">
                  {ministries.map((m) => {
                    const isSelected = form.ministerios_ids.includes(m.id);
                    return (
                      <Badge
                        key={m.id}
                        variant={isSelected ? "default" : "outline"}
                        className="cursor-pointer"
                        onClick={() => toggleMinistry(m.id)}
                        data-testid={`ministry-select-${m.id}`}
                      >
                        {m.nome}
                        {isSelected && <X className="h-3 w-3 ml-1" />}
                      </Badge>
                    );
                  })}
                </div>
                {form.ministerios_ids.length === 0 && (
                  <p className="text-xs text-muted-foreground">Clique para selecionar ministérios</p>
                )}
              </div>
            </div>

            <div className="flex gap-2 pt-4">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="flex-1" data-testid="save-function-button">
                Salvar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
